import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { DigitalCard } from "@/components/partner/DigitalCard";
import { copyText } from "@/lib/partners";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Download, ExternalLink, QrCode } from "lucide-react";

const LinkRow = ({ label, value, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between border-b border-gray-100 py-3">
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500">{label}</p>
      <p className="text-sm text-gray-700 truncate" data-testid={`${testid}-value`}>{value}</p>
    </div>
    <div className="flex gap-2 shrink-0">
      <Button data-testid={`copy-${testid}`} onClick={() => copyText(value, `${label} copied`)} variant="outline" size="sm" className="h-9 rounded-sm text-[11px] uppercase tracking-wider">
        <Copy size={12} className="mr-1.5" /> Copy
      </Button>
      <Button data-testid={`open-${testid}`} asChild variant="outline" size="sm" className="h-9 rounded-sm text-[11px] uppercase tracking-wider">
        <a href={value} target="_blank" rel="noreferrer"><ExternalLink size={12} /></a>
      </Button>
    </div>
  </div>
);

export const RealtorAssets = ({ realtor, programName }) => {
  const cardRef = useRef();
  const [busy, setBusy] = useState(false);
  const l = realtor.links;

  const exportCard = async (kind) => {
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 3, cacheBust: true, backgroundColor: "#0B0B0B" });
      if (kind === "png") {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `ark-${realtor.slug}-client-card.png`;
        a.click();
      } else {
        const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: [640, 800] });
        pdf.addImage(dataUrl, "PNG", 0, 0, 640, 800);
        pdf.save(`ark-${realtor.slug}-client-card.pdf`);
      }
      toast.success("Client card downloaded");
    } catch (e) {
      toast.error("Card export failed — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-10" data-testid="realtor-assets">
      <div>
        <h3 className="font-display text-xl mb-4">Links & tracking</h3>
        <div className="bg-white border border-gray-200 px-5 py-2">
          <LinkRow label="Realtor Presentation" value={l.presentation_url} testid="presentation-link" />
          <LinkRow label="Client Page" value={l.client_url} testid="client-link" />
          <LinkRow label="Permanent QR / Tracking Link" value={l.tracking_url} testid="tracking-link" />
          <div className="flex items-center justify-between py-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500">Tracking ID</p>
              <p className="font-display text-2xl tracking-[0.15em]">{realtor.referral_code}</p>
            </div>
            <span className="text-[11px] text-gray-400 max-w-[200px] text-right leading-relaxed">
              Printed QR codes keep working even if the slug changes.
            </span>
          </div>
        </div>

        <h3 className="font-display text-xl mt-10 mb-4 flex items-center gap-2"><QrCode size={18} /> QR code</h3>
        <div className="bg-white border border-gray-200 p-6 flex items-center gap-6">
          <img src={`${l.qr_image_url}?size=8`} alt="QR code" className="w-32 h-32" data-testid="realtor-qr-image" />
          <div className="space-y-2">
            <Button data-testid="download-qr-asset" asChild variant="outline" className="h-10 rounded-sm text-[11px] uppercase tracking-wider">
              <a href={`${l.qr_image_url}?size=16`} download={`ark-${realtor.slug}-qr.png`}><Download size={13} className="mr-1.5" /> Download PNG</a>
            </Button>
            <p className="text-[11px] text-gray-400 leading-relaxed max-w-[220px]">
              Scans route through {realtor.referral_code} then land on the client page.
            </p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-display text-xl mb-4">Digital client card</h3>
        <div className="bg-[#FAF7F2] border border-gray-200 p-6 flex flex-col items-center">
          <div style={{ width: 384, height: 480 }} className="overflow-hidden max-w-full">
            <div style={{ transform: "scale(0.6)", transformOrigin: "top left" }}>
              <DigitalCard ref={cardRef} realtor={realtor} links={l} programName={programName} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-6 justify-center">
            <Button data-testid="download-card-png" onClick={() => exportCard("png")} disabled={busy} className="h-10 bg-black hover:bg-black/85 text-white rounded-sm text-[11px] uppercase tracking-wider btn-lift">
              <Download size={13} className="mr-1.5" /> {busy ? "Rendering..." : "Download PNG"}
            </Button>
            <Button data-testid="download-card-pdf" onClick={() => exportCard("pdf")} disabled={busy} variant="outline" className="h-10 rounded-sm text-[11px] uppercase tracking-wider">
              <Download size={13} className="mr-1.5" /> Print-ready PDF
            </Button>
          </div>
        </div>

        <h3 className="font-display text-xl mt-10 mb-4">Ready-to-send client message</h3>
        <div className="bg-white border border-gray-200 p-5">
          <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed" data-testid="client-message-text">
            {realtor.outreach?.client_message}
          </p>
          <Button
            data-testid="copy-client-message"
            onClick={() => copyText(realtor.outreach?.client_message, "Client message copied")}
            variant="outline"
            className="mt-5 h-10 rounded-sm text-[11px] uppercase tracking-wider"
          >
            <Copy size={13} className="mr-1.5" /> Copy Message
          </Button>
        </div>
      </div>
    </div>
  );
};
